/**
 * TrendingService — Récupère les tendances mode actuelles via Claude IA
 *                   + identifie les produits partenaires qui matchent ces tendances.
 *
 * Flow :
 *  1. fetchTrendingTags(gender) → appelle /ai/trending-products → liste de tags trending
 *  2. matchProductsToTrends(products, trends) → enrichit chaque produit avec isTrending + trendTag
 *  3. getTrendingProducts(products, gender) → workflow complet (tendances + match + tri)
 *
 * Cache 6h côté backend (même tendances pour tous les users pendant 6h).
 * Cache 1h côté client (évite re-fetch inutile lors de navigation).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
// v1.12.117 — wrapper avec fallback localStorage sur web
import * as SecureStore from '../utils/secureStore';
import { AWS_CONFIG } from '../config/aws_config';
import { normalizedNameKey } from '../utils/productNameUtils';
import { mergeDuplicateProducts } from '../utils/mergeProducts';

const API = AWS_CONFIG.apiUrl;
// ═══ Cache client 24h ═══
// v3 = nouvelles tendances bilingues FR+EN + scoring +25/kw
// Bump le préfixe invalide automatiquement tous les v1/v2 empoisonnés
const CACHE_PREFIX = 'trending_cache_v3_';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h client-side

// ═══════════════════════════════════════════════════════════════
// RÉCUPÉRATION DES TAGS TENDANCE
// ═══════════════════════════════════════════════════════════════

/**
 * Récupère les tags tendance via l'API Claude.
 * @param {'F'|'M'} gender
 * @returns {Promise<Array<{tag, category, score, reason, keywords}>>}
 */
export async function fetchTrendingTags(gender = 'F', forceRefresh = false) {
  const cacheKey = `${CACHE_PREFIX}${gender}`;

  // Si force, skipper le cache
  if (forceRefresh) {
    console.log('[Trending] forceRefresh=true, skipping cache');
    try { await AsyncStorage.removeItem(cacheKey); } catch(e) {}
  }

  // Cache client (24h) — ignorer si vide (empoisonné par une erreur Lambda)
  if (!forceRefresh) {
    try {
      const raw = await AsyncStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (Date.now() - cached.ts < CACHE_TTL_MS && cached.trends && cached.trends.length > 0) {
          console.log('[Trending] cache client hit', cached.trends.length, 'tags');
          return cached.trends;
        }
        if (cached.trends && cached.trends.length === 0) {
          console.log('[Trending] cache was empty, clearing and re-fetching');
          try { await AsyncStorage.removeItem(cacheKey); } catch(e) {}
        }
      }
    } catch(e) {}
  }

  // Fetch backend
  try {
    const token = await SecureStore.getItemAsync('outfit_access');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;

    const r = await fetch(`${API}/ai/trending-products`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ gender, maxTags: 30, forceRefresh }),
    });

    if (!r.ok) {
      console.warn('[Trending] fetch failed', r.status);
      return [];
    }

    const json = await r.json();
    const trends = Array.isArray(json.trends) ? json.trends : [];

    console.log(`[Trending] ${trends.length} tendances récupérées (cache backend=${json.cached}, source=${json.source||'?'})`);

    // ═══ Save cache client SEULEMENT si résultat non vide ═══
    if (trends.length > 0) {
      try {
        await AsyncStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), trends }));
      } catch(e) {}
    } else {
      console.log('[Trending] empty result, NOT caching (will retry next call)');
    }

    return trends;
  } catch(e) {
    console.warn('[Trending] fetch error:', e.message);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════
// MATCHING PRODUITS ↔ TENDANCES
// ═══════════════════════════════════════════════════════════════

/**
 * Normalise un texte pour matching (lowercase, sans accents, sans ponctuation).
 */
function normalize(text) {
  if (!text) return '';
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // accents
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Calcule le score de match entre un produit et une tendance.
 * Retourne un score 0-100 basé sur :
 *  - Tag dans le nom produit : +50
 *  - Chaque keyword dans nom/catégorie/description : +10
 *  - Catégorie du produit matche catégorie tendance : +20
 */
function scoreProductTrendMatch(product, trend) {
  if (!product || !trend) return 0;

  const productText = normalize([
    product.name,
    product.category,
    product.subcategory,
    product.description,
    product.brand,
  ].filter(Boolean).join(' '));

  const tagNorm = normalize(trend.tag);
  const keywordsNorm = (trend.keywords || []).map(normalize).filter(k => k.length >= 3);

  let score = 0;

  // Tag complet dans le nom → gros boost
  if (tagNorm && productText.includes(tagNorm)) score += 50;

  // ═══ Chaque keyword trouvé : boost plus généreux ═══
  // +25 par keyword (avant: 10) → 2 keywords matchés suffisent pour passer le seuil (15)
  keywordsNorm.forEach(kw => {
    if (kw && productText.includes(kw)) score += 25;
  });

  // Catégorie matche ?
  if (trend.category && product.category) {
    const cat = normalize(product.category);
    const trendCat = normalize(trend.category);
    if (cat.includes(trendCat) || trendCat.includes(cat)) score += 20;
    // Mapping catégories FR/EN courantes
    const CAT_MAPPINGS = {
      'outerwear': ['veste', 'manteau', 'blazer'],
      'tops':      ['haut', 'tshirt', 'chemise', 'pull'],
      'bottoms':   ['pantalon', 'jean', 'short', 'jupe'],
      'dresses':   ['robe', 'habillage'],
      'shoes':     ['chaussure', 'basket', 'sneaker'],
      'accessories': ['accessoire', 'ceinture', 'echarpe'],
      'bags':      ['sac'],
    };
    const mappedFR = CAT_MAPPINGS[trendCat] || [];
    if (mappedFR.some(m => cat.includes(m))) score += 15;
  }

  return Math.min(score, 100);
}

/**
 * Enrichit une liste de produits avec des infos de tendance.
 * Ajoute sur chaque produit matchant :
 *  - isTrending: true
 *  - trendTag:  string (ex: "blazer oversized")
 *  - trendScore: number (popularité du trend)
 *  - trendReason: string
 *
 * @param {Array} products — produits partenaires existants
 * @param {Array} trends — tags tendance de fetchTrendingTags()
 * @returns {Array} produits enrichis
 */
export function enrichProductsWithTrends(products, trends) {
  if (!Array.isArray(products) || !Array.isArray(trends) || trends.length === 0) {
    console.log('[Trending] enrichment skipped: trends count=', trends?.length ?? 0);
    return products || [];
  }

  console.log(`[Trending] enriching ${products.length} products with ${trends.length} trends`);
  return enrichProductsWithTrendsCore(products, trends);
}

/**
 * ═══════════════════════════════════════════════════════════════
 * NOUVEAU : MATCH INCRÉMENTAL AVEC CACHE DELTA
 * ═══════════════════════════════════════════════════════════════
 *
 * Version async qui NE RECALCULE PAS les produits déjà scorés.
 *
 * Stratégie :
 *  1. Lire le cache `trending_match_v1_<gender>` (map productId → match)
 *  2. Signature des trends actuels (hash des tags) : si la sig change,
 *     invalider le cache (les tendances ont été refresh)
 *  3. Sinon, pour chaque produit :
 *     - Si déjà dans le cache → réutiliser (évite le scoring)
 *     - Si nouveau → scorer et ajouter au cache
 *  4. NETTOYER : retirer du cache les ids qui ne sont plus dans products
 *     → évite que le cache grossisse indéfiniment
 *  5. Sauvegarder le cache mis à jour
 *
 * Gain : sur 4520 produits dont 4500 déjà en cache + 20 nouveaux
 * → on ne score QUE les 20 nouveaux (au lieu de 4520).
 */
const MATCH_CACHE_PREFIX = 'trending_match_v1_';

function trendsSignature(trends) {
  // Hash léger basé sur les tags + keywords (ordre-insensible)
  const parts = (trends || []).map(t => {
    const kws = Array.isArray(t.keywords) ? [...t.keywords].sort().join(',') : '';
    return `${t.tag}|${kws}`;
  }).sort();
  return parts.join(';').slice(0, 400); // limite de taille
}

async function enrichProductsWithTrendsIncremental(products, trends, gender) {
  if (!Array.isArray(products) || !Array.isArray(trends) || trends.length === 0) {
    return products || [];
  }

  const cacheKey = `${MATCH_CACHE_PREFIX}${gender}`;
  const sig = trendsSignature(trends);
  let cachedMatches = {};
  let sameTrends = false;

  try {
    const raw = await AsyncStorage.getItem(cacheKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.sig === sig && parsed.matches && typeof parsed.matches === 'object') {
        cachedMatches = parsed.matches;
        sameTrends = true;
      }
    }
  } catch {}

  if (!sameTrends) {
    console.log('[Trending] 🔄 trends changed or cache empty → full rescore');
  }

  // Ids en cache
  const cachedIds = new Set(Object.keys(cachedMatches));
  // Ids actuels dans le catalogue
  const currentIds = new Set(products.map(p => p.id).filter(Boolean));

  // Produits nouveaux à scorer (pas dans le cache ou cache invalidé)
  let newScoredCount = 0;
  let reusedCount = 0;

  const enriched = products.map(p => {
    if (!p?.id) return p;

    // Cas 1 : déjà scoré, trends inchangés → réutiliser
    if (sameTrends && cachedMatches[p.id]) {
      reusedCount++;
      const m = cachedMatches[p.id];
      // Ne pas écraser les autres champs du produit
      return m.isTrending
        ? { ...p, isTrending: true, trendTag: m.trendTag, trendScore: m.trendScore, trendMatch: m.trendMatch, trendReason: m.trendReason }
        : p;
    }

    // Cas 2 : produit nouveau (ou trends changées) → scorer
    newScoredCount++;
    let bestMatch = null;
    let bestScore = 0;
    for (const trend of trends) {
      const score = scoreProductTrendMatch(p, trend);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = trend;
      }
    }

    // Seuil : 15 = matching faible mais réel
    if (bestMatch && bestScore >= 15) {
      const matchData = {
        isTrending:  true,
        trendTag:    bestMatch.tag,
        trendScore:  bestMatch.score || 50,
        trendMatch:  bestScore,
        trendReason: bestMatch.reason || '',
      };
      cachedMatches[p.id] = matchData;
      return { ...p, ...matchData };
    } else {
      cachedMatches[p.id] = { isTrending: false };
      return p;
    }
  });

  // ═══ Nettoyage : retirer du cache les ids qui n'existent plus ═══
  // Évite que le cache grossisse à l'infini (produits supprimés, brouillons, etc.)
  // Les produits disparus ne seront plus affichés car ils ne sont plus dans `products`.
  let removedCount = 0;
  for (const id of cachedIds) {
    if (!currentIds.has(id)) {
      delete cachedMatches[id];
      removedCount++;
    }
  }

  // Persister
  try {
    await AsyncStorage.setItem(cacheKey, JSON.stringify({
      sig,
      matches: cachedMatches,
      ts: Date.now(),
    }));
  } catch(e) { console.warn('[Trending] cache save failed:', e.message); }

  console.log(`[Trending] 📊 incremental: ${reusedCount} réutilisés, ${newScoredCount} nouveaux, ${removedCount} obsolètes retirés`);
  return enriched;
}

/**
 * Version SYNC historique (conservée pour CatalogScreen qui l'appelle
 * directement en mode synchrone avec trends déjà en cache). Identique au
 * comportement précédent, mais refactorée pour partager la logique.
 */
function enrichProductsWithTrendsCore(products, trends) {
  if (!Array.isArray(products) || !Array.isArray(trends) || trends.length === 0) {
    return products || [];
  }

  console.log(`[Trending] enriching ${products.length} products with ${trends.length} trends`);
  if (trends[0]) {
    console.log(`[Trending] sample trend:`, JSON.stringify({
      tag: trends[0].tag,
      category: trends[0].category,
      keywords: trends[0].keywords,
    }));
  }

  // ═══ DIAGNOSTIC : inspecter le 1er produit pour voir ses champs ═══
  if (products[0]) {
    const p = products[0];
    console.log(`[Trending] sample product[0]:`, JSON.stringify({
      name: p.name,
      category: p.category,
      subcategory: p.subcategory,
      brand: p.brand,
      hasDesc: !!p.description,
    }));
    console.log(`[Trending] sample product[0] normalized text:`, normalize([
      p.name, p.category, p.subcategory, p.description, p.brand,
    ].filter(Boolean).join(' ')).slice(0, 120));
  }
  // Cherchons un produit avec "blazer" ou "jean" dans le nom
  const searchTerms = ['blazer', 'jean', 'ballerine', 'chemise', 'robe'];
  for (const term of searchTerms) {
    const found = products.find(p => {
      const txt = normalize([p.name, p.category, p.subcategory].filter(Boolean).join(' '));
      return txt.includes(term);
    });
    if (found) {
      console.log(`[Trending] ✓ found product with "${term}":`, found.name?.slice(0, 60), '| cat=', found.category);
      break;
    } else {
      console.log(`[Trending] ✗ NO product contains "${term}"`);
    }
  }

  let matched = 0;
  let scoreDistribution = { 0: 0, '1-19': 0, '20-39': 0, '40-59': 0, '60+': 0 };

  const result = products.map(p => {
    let bestMatch = null;
    let bestScore = 0;

    for (const trend of trends) {
      const score = scoreProductTrendMatch(p, trend);
      if (score > bestScore && score >= 15) { // seuil bas pour ratisser large + catégorie FR/EN
        bestScore = score;
        bestMatch = trend;
      }
    }

    // Stats
    if (bestScore === 0) scoreDistribution[0]++;
    else if (bestScore < 20) scoreDistribution['1-19']++;
    else if (bestScore < 40) scoreDistribution['20-39']++;
    else if (bestScore < 60) scoreDistribution['40-59']++;
    else scoreDistribution['60+']++;

    if (bestMatch) {
      matched++;
      return {
        ...p,
        isTrending:  true,
        trendTag:    bestMatch.tag,
        trendScore:  bestMatch.score,
        trendReason: bestMatch.reason,
        trendMatch:  bestScore, // score du match produit↔tendance
      };
    }

    return p;
  });

  console.log(`[Trending] enrichment done: ${matched}/${products.length} matched. Scores:`, JSON.stringify(scoreDistribution));
  return result;
}

// ═══════════════════════════════════════════════════════════════
// WORKFLOW COMPLET
// ═══════════════════════════════════════════════════════════════

/**
 * Utilitaire : vérifier si un produit est trending.
 * Règle unique : ai_metadata.isTrendy === true
 *
 * Utilisé par CatalogScreen pour trier produits dans chaque onglet catégorie
 * (trending-first). Pas d'effet de bord, fonction pure.
 */
export function isProductTrending(product) {
  if (!product) return false;
  // Source de vérité : analyse Claude Vision
  if (product?.ai_metadata?.isTrendy === true) return true;
  // Fallback : champ isTrending déjà calculé (si enrichProductsWithTrends a tourné)
  if (product?.isTrending === true) return true;
  return false;
}

/**
 * Workflow trending — basé UNIQUEMENT sur ai_metadata.isTrendy
 *
 * Règle unique :
 *  - Un produit est trending ⟺ son ai_metadata.isTrendy === true
 *  - ai_metadata est posé une seule fois par Claude Vision lors de
 *    l'analyse de l'image (endpoint /analyze-catalog-item)
 *  - Pas de matching par tags, pas d'heuristique rating/discount :
 *    l'analyse IA est la source unique de vérité
 *
 * Pour qu'un produit ait ai_metadata.isTrendy :
 *  1. L'app appelle enrichCatalogWithAI(products, token) qui fetch
 *     /catalog-analysis pour les metadata déjà en DB
 *  2. En parallèle, analyzeCatalogInBackground lance des batchs Claude
 *     pour les produits pas encore analysés (5-10 par session)
 *  3. Au fur et à mesure des sessions, le catalogue se remplit
 *
 * Usage :
 *   const { products, trendingCount } = await getTrendingProducts(partnerProducts, 'F');
 */
export async function getTrendingProducts(products, gender = 'F') {
  console.log(`[Trending] ▶ getTrendingProducts called: ${products?.length || 0} products, gender=${gender}`);

  // v1.12.375 — Filtre genre STRICT AVANT enrichissement.
  //   v1.12.373 utilisait filterByGenderAI (permissif → garde les ambigus).
  //   Résultat : produits femme sans ai_metadata.gender ni signal textuel
  //   passaient en trending pour profil homme. Maintenant strict :
  //     1. priorité ai_metadata.gender (Claude Vision)
  //     2. fallback regex stricte
  //     3. AMBIGU → REJETÉ pour trending (préfère moins d'items qu'incohérents)
  let scoped = products || [];
  if (gender === 'M' || gender === 'F') {
    try {
      const { detectProductGender } = require('./GenderFilter');
      const before = scoped.length;
      scoped = scoped.filter(p => {
        const aiG = p?.ai_metadata?.gender;
        if (aiG === 'F' || aiG === 'M') return aiG === gender;
        if (aiG === 'unisex') return true;
        // Pas d'IA → regex
        const g = detectProductGender(p);
        if (g === null) return false; // strict : on rejette les ambigus
        return g === gender;
      });
      if (scoped.length !== before) console.log(`[Trending] 🚻 strict gender filter: ${scoped.length}/${before} (target=${gender})`);
    } catch (e) {
      console.warn('[Trending] gender filter unavailable:', e?.message);
    }
  }

  // ═══ DIAGNOSTIC : couverture ai_metadata ═══
  const withMeta      = scoped.filter(p => p?.ai_metadata);
  const withIsTrendy  = withMeta.filter(p => p.ai_metadata.isTrendy === true);
  console.log(`[Trending] 🔍 diag: ai_metadata=${withMeta.length}/${scoped.length}, isTrendy=true sur ${withIsTrendy.length}`);

  // ═══ Enrichissement des produits avec isTrending depuis ai_metadata ═══
  // v1.12.338 — Partenaires premium (SUIT/ANN/COUTR) auto-trending par défaut
  //   Raison : ces partenaires luxe valent la peine d'être mis en avant même
  //   si pas encore taggés isTrendy par Claude (COUTR n'a pas d'analyse IA).
  //   Boost prudent : trendScore=55 (juste au-dessus du seuil 50).
  const AUTO_TRENDING_PARTNERS = new Set(['suit_negozi', 'ann_demeulemeester', 'coutr']);
  const enriched = scoped.map(p => {
    const meta = p?.ai_metadata;
    if (meta && meta.isTrendy === true) {
      return {
        ...p,
        isTrending:  true,
        trendScore:  meta.trendScore || 50,
        trendTag:    meta.trendTag || '',
        trendReason: meta.trendReason || '',
      };
    }
    // Fallback : partenaire premium → semi-trending automatique
    if (p && AUTO_TRENDING_PARTNERS.has(p.partner)) {
      return {
        ...p,
        isTrending:  true,
        trendScore:  55,
        trendTag:    '',
        trendReason: '',
      };
    }
    return p;
  });

  // Tri : trending d'abord (par trendScore desc), puis le reste
  const trendingItems = enriched.filter(p => p.isTrending);
  const others        = enriched.filter(p => !p.isTrending);
  trendingItems.sort((a, b) => (b.trendScore || 0) - (a.trendScore || 0));

  console.log(`[Trending] ✓ ${trendingItems.length}/${scoped.length} trending (ai_metadata.isTrendy)`);

  // ═══ BOOST PRÉFÉRENCES DU JOUR ═══
  // Si l'user a saisi des prefs au cold start, on remonte les trending qui
  // matchent (catégorie cherchée, style coché, occasion, budget) en tête de liste.
  let finalTrending = trendingItems;
  try {
    const { getCachedPrefs } = require('./DailyPreferencesService');
    const prefs = getCachedPrefs();
    if (prefs && (prefs.categories?.length || prefs.styles?.length || prefs.occasion || prefs.budget)) {
      finalTrending = applyPrefsBoostToTrending(trendingItems, prefs);
      console.log(`[Trending] daily prefs applied (cats=${prefs.categories?.length || 0}, styles=${prefs.styles?.length || 0})`);
    }
  } catch {}

  if (withMeta.length === 0 && scoped.length > 10) {
    console.warn(`[Trending] ⚠️ AUCUN produit n'a ai_metadata — l'analyse Claude doit tourner en arrière-plan via analyzeCatalogInBackground. Patience : le catalogue se remplit au fil des sessions.`);
  }

  return {
    products:      [...finalTrending, ...others],
    trendingCount: finalTrending.length,
    trends:        [],
    source:        'ai_metadata',
  };
}

/**
 * Re-classe les articles trending selon l'affinité avec les préférences user.
 * Ne supprime aucun article : les non-matchants restent en queue de liste.
 *
 * v1.9.40 : Budget par pièce + boost renforcé pour style/occasion
 *
 * @param {Array} items   articles isTrending=true
 * @param {Object} prefs  { categories, styles, occasion, budget }
 */
export function applyPrefsBoostToTrending(items, prefs) {
  if (!Array.isArray(items) || items.length === 0) return items;

  // Budget = MAX par pièce (cohérent avec Sélection styliste v1.9.36)
  const BUDGET_MAX_PER_ITEM = {
    'budget':    50,
    'mid-range': 150,
    'premium':   500,
    'luxury':    99999,
  };
  const maxPrice = prefs.budget ? BUDGET_MAX_PER_ITEM[prefs.budget] : null;

  return items
    .map(item => {
      let bonus = 0;
      const ai = item.ai_metadata || {};

      // +20 si catégorie cherchée (boost renforcé)
      if (prefs.categories?.length > 0 && prefs.categories.includes(item.category)) {
        bonus += 20;
      }

      // +15 si un style coché matche les styleScores élevés (>=60)
      if (prefs.styles?.length > 0) {
        const styleScores = ai.styleScores || {};
        const hasMatch = prefs.styles.some(s => (styleScores[s] || 0) >= 60);
        if (hasMatch) bonus += 15;
        // Bonus partiel si match >= 40
        else {
          const partialMatch = prefs.styles.some(s => (styleScores[s] || 0) >= 40);
          if (partialMatch) bonus += 8;
        }
      }

      // +12 si occasion matche
      if (prefs.occasion) {
        const occScores = ai.occasions || {};
        if ((occScores[prefs.occasion] || 0) >= 60) bonus += 12;
        else if ((occScores[prefs.occasion] || 0) >= 40) bonus += 6;
      }

      // +8 si prix dans la limite (≤ max par pièce)
      if (maxPrice) {
        const price = parseFloat(item.price) || parseFloat(item.salePrice) || 0;
        if (price > 0 && price <= maxPrice) {
          bonus += 8;
        }
      }

      const finalScore = (item.trendScore || 50) + bonus;
      return { ...item, _prefsBoostScore: finalScore };
    })
    .sort((a, b) => (b._prefsBoostScore || 0) - (a._prefsBoostScore || 0))
    .map(({ _prefsBoostScore, ...item }) => item); // retirer le champ tampon
}

/**
 * Sélectionne les N produits les plus tendance pour afficher une section "🔥 Tendances".
 * @param {Array} products — produits enrichis (via getTrendingProducts)
 * @param {number} n — nombre max
 */
export function pickTopTrending(products, n = 10) {
  if (!Array.isArray(products)) return [];
  return products
    .filter(p => p.isTrending)
    .sort((a, b) => {
      const sa = (a.trendScore || 0) + (a.trendMatch || 0);
      const sb = (b.trendScore || 0) + (b.trendMatch || 0);
      return sb - sa;
    })
    .slice(0, n);
}

// ═══════════════════════════════════════════════════════════════
// CATÉGORIES POUR DIVERSIFICATION
// ═══════════════════════════════════════════════════════════════
// Mapping catégories produits FR → buckets normalisés
// (pour répartir les tendances entre robe, haut, bas, chaussures, etc.)
const CAT_BUCKETS = {
  // Robes
  'robe':         'dresses',
  'robes':        'dresses',
  'habillage':    'dresses',
  'dress':        'dresses',
  // Hauts
  'haut':         'tops',
  'hauts':        'tops',
  't-shirt':      'tops',
  'tshirt':       'tops',
  'tee-shirt':    'tops',
  'chemise':      'tops',
  'chemisier':    'tops',
  'pull':         'tops',
  'sweat':        'tops',
  'blouse':       'tops',
  'top':          'tops',
  'debardeur':    'tops',
  // Bas
  'bas':          'bottoms',
  'bas-femme':    'bottoms',
  'bas-homme':    'bottoms',
  'pantalon':     'bottoms',
  'pantalons':    'bottoms',
  'jean':         'bottoms',
  'jeans':        'bottoms',
  'short':        'bottoms',
  'shorts':       'bottoms',
  'jupe':         'bottoms',
  'jupes':        'bottoms',
  'legging':      'bottoms',
  // Vêtements d'extérieur
  'manteau':      'outerwear',
  'manteaux':     'outerwear',
  'veste':        'outerwear',
  'vestes':       'outerwear',
  'blazer':       'outerwear',
  'parka':        'outerwear',
  'trench':       'outerwear',
  'cape':         'outerwear',
  'doudoune':     'outerwear',
  // Chaussures
  'chaussure':    'shoes',
  'chaussures':   'shoes',
  'basket':       'shoes',
  'baskets':      'shoes',
  'sneaker':      'shoes',
  'sneakers':     'shoes',
  'bottes':       'shoes',
  'botte':        'shoes',
  'bottines':     'shoes',
  'escarpin':     'shoes',
  'escarpins':    'shoes',
  'ballerine':    'shoes',
  'ballerines':   'shoes',
  'mocassin':     'shoes',
  'mocassins':    'shoes',
  // Sacs
  'sac':          'bags',
  'sacs':         'bags',
  'sac a main':   'bags',
  'sac a dos':    'bags',
  // Bijoux (séparé des accessoires)
  'bijou':        'jewelry',
  'bijoux':       'jewelry',
  'collier':      'jewelry',
  'colliers':     'jewelry',
  'bague':        'jewelry',
  'bagues':       'jewelry',
  'bracelet':     'jewelry',
  'bracelets':    'jewelry',
  'boucles':      'jewelry',
  'montre':       'jewelry',
  'montres':      'jewelry',
  // Accessoires
  'accessoire':   'accessories',
  'accessoires':  'accessories',
  'ceinture':     'accessories',
  'ceintures':    'accessories',
  'echarpe':      'accessories',
  'echarpes':     'accessories',
  'lunettes':     'accessories',
  'chapeau':      'accessories',
  'chapeaux':     'accessories',
  'bonnet':       'accessories',
  'bonnets':      'accessories',
  'casquette':    'accessories',
  'foulard':      'accessories',
};

function getBucket(product) {
  if (!product?.category) return 'other';
  const cat = String(product.category).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  // Match direct
  if (CAT_BUCKETS[cat]) return CAT_BUCKETS[cat];
  // Match par sous-chaîne
  for (const [key, bucket] of Object.entries(CAT_BUCKETS)) {
    if (cat.includes(key)) return bucket;
  }
  return 'other';
}

/**
 * Sélection DIVERSIFIÉE : répartit les N tendances sur plusieurs catégories
 * Évite d'avoir 20 baskets d'affilée dans "Tendances du moment"
 *
 * Algorithme round-robin :
 *   1. On groupe les produits trending par catégorie (tops, bottoms, dresses, shoes...)
 *   2. On prend 1 produit de chaque catégorie à tour de rôle
 *   3. Les catégories sont ordonnées par score global moyen
 *
 * @param {Array} products — produits enrichis (via getTrendingProducts)
 * @param {number} n — nombre max (défaut 12, jusqu'à 50)
 * @returns {Array}
 */
/**
 * Sélectionne ~N produits trending diversifiés par catégorie.
 *
 * Mode normal (preferredCategories vide ou null) :
 *   Round-robin sur toutes les catégories, ordre par score moyen du top 3.
 *
 * Mode "filtrage doux" (preferredCategories non vide) :
 *   1. Les produits dont category ∈ preferredCategories sont AFFICHÉS EN PREMIER
 *      (round-robin entre les seules catégories préférées, triées par score)
 *   2. PUIS, en complément si on n'a pas atteint N, les autres catégories
 *      sont ajoutées en round-robin jusqu'à atteindre N.
 *
 * Ainsi quand l'user coche "Hauts" + "Vestes" dans les daily prefs :
 *   - les Hauts/Vestes trending apparaissent d'abord
 *   - puis les autres catégories trending complètent jusqu'à N
 *
 * @param {Array} products
 * @param {number} n
 * @param {Array<string>} [preferredCategories] — catégories à prioriser (filtrage doux)
 */
export function pickDiversifiedTrending(products, n = 12, preferredCategories = null, opts = {}) {
  if (!Array.isArray(products)) return [];
  // ═══ v1.9.42 : option `strict` ═══
  // Si strict=true et que des préférences sont passées, on n'affiche QUE les
  // préférés (pas de complément avec d'autres catégories). Idéal pour quand
  // l'user a explicitement filtré par catégories accessoires (Bijoux, Vestes...).
  const strict = opts.strict === true;

  // ═══ 1. MERGE doublons : agrège les tailles au lieu de supprimer ═══
  // Cas iQueens : même article scrapé en plusieurs entrées (1 par taille).
  // mergeDuplicateProducts() fusionne sur (brand+nom_normalisé+prix) et
  // collecte les sizes de tous les doublons → 1 produit final avec toutes
  // les tailles dispo dans son champ `sizes`.
  const dedupedAll = mergeDuplicateProducts(products);
  if (dedupedAll.length !== products.length) {
    console.log(`[Trending] merged ${products.length - dedupedAll.length} doublons (sizes agrégées)`);
  }

  const trending = dedupedAll.filter(p => p.isTrending && p.image && p.price > 0);
  const hasPrefs = Array.isArray(preferredCategories) && preferredCategories.length > 0;

  // ═══ Helper local : clé pour identifier un produit déjà picked ═══
  // Utilisé par roundRobin et le mode "filtrage doux" pour ne pas reprendre
  // un produit déjà sélectionné dans une étape précédente. Cohérent avec la
  // clé de mergeDuplicateProducts (brand + nom normalisé + prix).
  const dedupKey = (p) => {
    const name  = normalizedNameKey(p?.name || '');
    const brand = String(p?.brand || p?.partner || '').trim().toLowerCase();
    const price = Number(p?.price) || 0;
    return `${brand}|${name}|${price}`;
  };

  // ═══ 2. Mapping catégorie pref → bucket interne ═══
  // Les prefs daily contiennent des labels FR ('Robes', 'Hauts', 'Vestes', etc.)
  // mais p.category dans les produits peut être en singulier, en anglais, ou plus
  // spécifique ('robe longue'). On utilise getBucket pour normaliser des deux côtés.
  const preferredBuckets = hasPrefs
    ? new Set(preferredCategories.map(c => getBucket({ category: c })).filter(b => b !== 'other'))
    : null;

  console.log(`[Trending] 🎯 pickDiversifiedTrending: ${trending.length}/${dedupedAll.length} trending (after dedup), target=${n}${hasPrefs ? `, prefs=[${preferredCategories.join(',')}] → buckets=[${[...preferredBuckets].join(',')}]` : ''}`);
  if (trending.length === 0) return [];

  // Helper : round-robin entre buckets, triés par score moyen top3
  const roundRobin = (items, limit, alreadyPicked = new Set()) => {
    const byBucket = {};
    items.forEach(p => {
      // Skip déjà picked (utilise dedupKey pour comparaison fiable, pas la référence)
      if (alreadyPicked.has(dedupKey(p))) return;
      const bucket = getBucket(p);
      if (!byBucket[bucket]) byBucket[bucket] = [];
      byBucket[bucket].push(p);
    });
    // Tri par score décroissant dans chaque bucket
    Object.values(byBucket).forEach(arr => arr.sort((a, b) => {
      const sa = (a.trendScore || 0) + (a.trendMatch || 0);
      const sb = (b.trendScore || 0) + (b.trendMatch || 0);
      return sb - sa;
    }));
    // Ordre des buckets : score moyen top 3 décroissant
    const buckets = Object.keys(byBucket).sort((a, b) => {
      const avgA = byBucket[a].slice(0, 3).reduce((s, p) => s + (p.trendScore || 0) + (p.trendMatch || 0), 0) / Math.min(3, byBucket[a].length);
      const avgB = byBucket[b].slice(0, 3).reduce((s, p) => s + (p.trendScore || 0) + (p.trendMatch || 0), 0) / Math.min(3, byBucket[b].length);
      return avgB - avgA;
    });
    // Round-robin
    const result = [];
    const indexes = Object.fromEntries(buckets.map(b => [b, 0]));
    let added = true;
    while (result.length < limit && added) {
      added = false;
      for (const bucket of buckets) {
        if (result.length >= limit) break;
        const idx = indexes[bucket];
        if (idx < byBucket[bucket].length) {
          result.push(byBucket[bucket][idx]);
          indexes[bucket] = idx + 1;
          added = true;
        }
      }
    }
    return { result, bucketsUsed: buckets };
  };

  // ═══ Mode "filtrage doux" : préférés d'abord, autres en complément ═══
  if (hasPrefs && preferredBuckets.size > 0) {
    // Filtrage par BUCKET (et non par .category brut) → robust à 'robe'/'Robes'/etc.
    const preferred = trending.filter(p => preferredBuckets.has(getBucket(p)));
    const others    = trending.filter(p => !preferredBuckets.has(getBucket(p)));

    // ═══ Diagnostic par bucket préféré ═══
    // Pour chaque bucket coché, on log combien de produits trending on a réellement.
    // Si un bucket a 0 produits trending, on tente une "réserve" : produits du
    // catalogue avec la bonne catégorie même s'ils ne sont pas marqués isTrending.
    const trendingByPrefBucket = {};
    for (const b of preferredBuckets) {
      trendingByPrefBucket[b] = trending.filter(p => getBucket(p) === b).length;
    }
    console.log(`[Trending] 🎯 par bucket coché:`, JSON.stringify(trendingByPrefBucket));

    // ═══ Réserve : si un bucket coché n'a aucun ou très peu de trending,
    //     prendre des produits non-trending de ce bucket pour l'enrichir. ═══
    // Évite que l'user qui coche "Vestes" voie 0 veste juste parce qu'aucune
    // veste n'a matché un trend (peut arriver si le catalog est limité).
    const TARGET_PER_BUCKET = Math.max(2, Math.floor(n / Math.max(2, preferredBuckets.size)));
    const reservePicks = [];
    for (const b of preferredBuckets) {
      const have = trendingByPrefBucket[b] || 0;
      if (have < TARGET_PER_BUCKET) {
        // Chercher des produits du bucket dans dedupedAll (pas filtré sur isTrending)
        const reserve = dedupedAll
          .filter(p => p.image && p.price > 0 && getBucket(p) === b && !p.isTrending)
          .slice(0, TARGET_PER_BUCKET - have);
        if (reserve.length > 0) {
          console.log(`[Trending] 🎒 bucket "${b}" sous-représenté (${have}/${TARGET_PER_BUCKET}) → +${reserve.length} produits non-trending injectés`);
          reservePicks.push(...reserve);
        }
      }
    }
    // On ajoute la réserve au pool des préférés (ils auront un trendScore=0 donc
    // seront classés en bas de leur bucket, mais ils REPRÉSENTERONT la catégorie).
    const preferredAugmented = [...preferred, ...reservePicks];

    // Étape 1 : remplir avec les préférés (+ réserve)
    const { result: preferredResult, bucketsUsed: prefBuckets } = roundRobin(preferredAugmented, n);

    // Étape 2 : compléter avec les autres si on n'a pas atteint n
    // ═══ v1.9.42 : si strict=true, on N'AJOUTE PAS d'autres catégories ═══
    let result = preferredResult;
    if (!strict && result.length < n) {
      const pickedIds = new Set(result.map(p => dedupKey(p)));
      const { result: otherResult, bucketsUsed: otherBuckets } = roundRobin(others, n - result.length, pickedIds);
      result = [...result, ...otherResult];
      console.log(`[Trending] 🎯 soft filter: ${preferredResult.length} preferred (${prefBuckets.join(', ')}) + ${otherResult.length} others (${otherBuckets.join(', ')})`);
    } else {
      console.log(`[Trending] 🎯 ${strict ? 'STRICT' : 'soft'} filter: ${preferredResult.length} preferred (${prefBuckets.join(', ')})${strict ? ' — pas de complément' : ' — limite atteinte sans complément'}`);
    }
    return result;
  }

  // ═══ Mode normal : round-robin global ═══
  const { result, bucketsUsed } = roundRobin(trending, n);
  console.log(`[Trending] 🎯 diversified pick: ${result.length} items across ${bucketsUsed.length} categories (${bucketsUsed.join(', ')})`);
  return result;
}

/**
 * Invalide le cache client (ex: après un refresh manuel).
 * Purge à la fois :
 *  - trending_cache_v3_* (tags Claude/backend — 24h)
 *  - trending_match_v1_* (matches produits — delta incrémental)
 */
export async function invalidateTrendingCache() {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const trendingKeys = keys.filter(k =>
      k.startsWith(CACHE_PREFIX) || k.startsWith(MATCH_CACHE_PREFIX)
    );
    await AsyncStorage.multiRemove(trendingKeys);
    console.log(`[Trending] cache invalidated: ${trendingKeys.length} keys`);
  } catch(e) {}
}
